import React, { useState, useEffect } from 'react';
import styled, { useTheme, createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Sparkles, Search, X, Lock } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import menuConfig from './MenuConfig';
import GradientIcon from '../ui/GradientIcon';
import { userFlagGate, hrOrUserFlagGate, filterAccessible, findActiveTrail, searchMenu, lockGated, enterpriseAdminGate, hrGate, moduleSubscriptionGate, superAdminHiddenGate } from './menuTree';
import usePerformanceInboxBadge from '../../hooks/usePerformanceInboxBadge';
import { formatBadgeCount } from '../../hooks/performanceInboxBadge';
import useApprovalsBadge from '../../hooks/useApprovalsBadge';
import { formatBadgeCount as formatApprovalsBadgeCount } from '../../hooks/approvalsBadge';
import { hasAnyApprovalCategory } from '../../pages/approvals/config/categories';
import { resolveCanUsePm } from '../../pages/dashboard/resolveCanUsePm';

const Dot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#C0C8D0')};
  flex-shrink: 0;
`;

// Bekleyen iş sayacı — menü öğesinin sağ ucunda dolu bir hap. Menü ikon dili
// gradyandır; rozet de aynı dili taşır (bkz. GradientIcon), seviye-0 aktif
// öğede olduğu gibi zeminle çakışmasın diye metin her zaman beyazdır.
const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary.main}, ${({ theme }) => theme.colors.secondary.main});
  color: #fff;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  line-height: 1;
  flex-shrink: 0;
`;

const SearchBox = styled.div`
  padding: 10px 12px 6px;
`;

// Modern pill search field: a light rounded input with a gradient-filled
// circular "search" badge on the trailing (right) edge — a white magnifier on
// the brand gradient. The whole field is a <label>, so clicking anywhere focuses
// the input; a clear (×) button appears once there's a query. On focus the fill
// brightens to white and a soft shadow lifts the field.
const SearchField = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 16px;
  border: 1px solid #e8ecf0;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgColor};
  cursor: text;

  @media (prefers-reduced-motion: no-preference) {
    transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.2s ease;
  }

  &:focus-within {
    background: #fff;
    border-color: ${({ theme }) => theme.colors.primary.light};
    box-shadow: 0 2px 10px rgba(2, 145, 239, 0.1);
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 0;
    border: none;
    background: transparent;
    outline: none;
    font-family: ${({ theme }) => theme.metrics.font.family};
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  input::placeholder {
    color: ${({ theme }) => theme.colors.text.light.little};
  }
`;

// The trailing gradient "search" badge — a colored circle carrying a white
// magnifier. Decorative (results filter live as you type); the wrapping
// <label> provides click-to-focus.
const SearchAction = styled.span`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary.main},
    ${({ theme }) => theme.colors.secondary.main}
  );
  box-shadow: 0 2px 8px rgba(2, 145, 239, 0.3);
`;

const ClearButton = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;

  @media (prefers-reduced-motion: no-preference) {
    transition: background 0.15s ease, color 0.15s ease;
  }

  &:hover {
    background: rgba(2, 145, 239, 0.1);
    color: ${({ theme }) => theme.colors.primary.main};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.main};
    outline-offset: 1px;
  }
`;

const NoResults = styled.li`
  padding: 12px 20px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 14px;
`;

// Kilitli TD grubunun yaprak ikonu — Dot yerine soluk kilit rozeti.
const LockIcon = styled(Lock)`
  color: ${({ theme }) => theme.colors.text.dark.little};
  flex-shrink: 0;
`;

// Category (level-0, variant:'category') headers render as a muted,
// letter-spaced "section header" — visually distinct from module/page rows.
// Scoped to `.menu-category`'s OWN button via the child combinator: react-pro-sidebar
// renders <li class="ps-menuitem-root ps-submenu-root [+ our menu-category]"> with
// <MenuButton class="ps-menu-button"> and <SubMenuContent class="ps-submenu-content">
// as direct-child siblings (see node_modules/react-pro-sidebar/dist/index.es.js,
// StyledSubMenu). Nested module/page rows live inside ps-submenu-content, one level
// deeper — never as a direct child of the category's own <li> — so
// `.menu-category > .ps-menu-button` can only ever match the category's own button,
// and `.menu-category .ps-menu-label` (no child combinator) would incorrectly reach
// every label nested underneath instead.
// text-transform needs !important: assets/styles/global.js applies
// `* { text-transform: none !important; }` app-wide, which would otherwise
// silently defeat this rule (no build/runtime error — it just never renders).
const CategoryLabelStyle = createGlobalStyle`
  .menu-category > .ps-menu-button .ps-menu-label {
    text-transform: uppercase !important;
    letter-spacing: 0.04em;
    font-size: ${({ theme }) => theme.metrics.font.size.xs};
    color: ${({ theme }) => theme.colors.text.dark.little};
  }
`;

// FE-08 — `onNavigate`: dar ekranda kenar çubuğu içeriğin ÜSTÜNE binen bir
// katmandır. Bir menü öğesine dokunulduğunda kapanmazsa kullanıcı gittiği
// sayfayı hiç göremez, menüyü elle kapatması gerekir. Masaüstünde bu prop
// verilmez (menü kalıcıdır) ve davranış değişmez.
export default function SidebarMenu({ collapsed, onNavigate }) {
  const { t } = useTranslation();
  const { isSuperAdmin, isEnterpriseAdmin, isHr, canManageTenants, hasModule, hasSeat, canRead, canWrite, user, isPreboardingLocked } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  // Rozet YALNIZ PM aboneliği + PM KOLTUĞU + kilitsiz oturum varken veri çeker;
  // aksi halde hiç istek atılmaz. Ruling 64: koltuk koşulu olmasaydı ölçümlü PM'de
  // koltuksuz her çalışan her açılışta /performance/inbox/count 403'ü + refresh-session
  // turu üretirdi. Ölçümsüzde hasSeat true döner (bugünkü gibi).
  const inboxBadge = formatBadgeCount(
    usePerformanceInboxBadge(resolveCanUsePm({ hasModule, hasSeat }) && !isPreboardingLocked),
  );

  // Onaylarım rozeti — AYNI koşul (hasAnyApprovalCategory) menü öğesinin
  // görünürlüğünü de belirler (bkz. hasAccess'teki 'onaylarim' özel dalı):
  // hiçbir onay kategorisi yoksa öğe hiç görünmez, rozet de hiç istek atmaz.
  const canSeeApprovals = hasAnyApprovalCategory({ hasModule, canRead, canWrite, isSuperAdmin });
  const approvalsBadge = formatApprovalsBadgeCount(
    useApprovalsBadge(canSeeApprovals && !isPreboardingLocked),
  );

  const hasAccess = (item) => {
    // Process-scoped self-service items (e.g. "İşten Ayrılışım") only make sense
    // while the user is actually in that process. Hidden — even for admins —
    // unless the server-derived user flag is set; a filtered-empty parent group
    // is then pruned by filterAccessible, so a non-offboarding employee sees no
    // offboarding menu at all (customer feedback).
    if (item.requiresUserFlag && !user?.[item.requiresUserFlag]) return false;
    // Onaylarım artık yaprak: hiçbir onay kategorisi yoksa (modül/izin
    // eksikliği) menüde hiç görünmez — bkz. pages/approvals/config/categories.js.
    if (item.key === 'onaylarim' && !canSeeApprovals) return false;
    // İzin Bakiye Raporu (bkz. MenuConfig'teki not): LM.Write VEYA astı olan
    // herkes görür; ikisi de yoksa gizli — backend zaten 403 döner, menü boşa
    // düşmesin. superAdmin/enterpriseAdmin canWrite bypass'ından geçer.
    if (item.key === 'leave-balance-report' && !(canWrite('LM') || user?.hasSubordinates)) return false;
    // Bilinçli çift yolun kiracı girişi superAdmin'den gizlenir — aynı sayfa onun platform girişinde
    // (inceleme M13b: Tatil Takvimleri superAdmin menüsünde iki kez görünüyordu). Baypastan ÖNCE.
    if (!superAdminHiddenGate(item, { isSuperAdmin })) return false;
    if (isSuperAdmin) return true;
    // Görev 26 (B5) — sunucu tarafı yalnız aktif enterpriseAdmin'e (İK) veri
    // dönen yüzeyler (PIP süreçleri, sonuç itirazları). PM.Read tek başına
    // yeterli DEĞİL: menü görünür kalırsa kullanıcı kalıcı olarak BOŞ bir
    // listeye düşer. superAdmin yukarıda zaten geçti (tüm kontroller baypas).
    // Kural SAF fonksiyonda (menuTree.enterpriseAdminGate), testi orada.
    if (!enterpriseAdminGate(item, { isEnterpriseAdmin, isSuperAdmin })) return false;
    // İK rolü ayrımı — yalnız İK'ya veri dönen PM yüzeyleri (menuTree.hrGate, testi orada).
    if (!hrGate(item, { isHr, isSuperAdmin })) return false;
    // Analitik Plan 3 — PM raporları: İK VEYA astı olan (menuTree.hrOrUserFlagGate, testi orada).
    if (!hrOrUserFlagGate(item, { isHr, isSuperAdmin }, user)) return false;
    // Relocated self-service/approval items carry an explicit module code so they
    // stay gated by tenant subscription even though they live outside their module group.
    if (item.module && !hasModule(item.module)) return false;
    // Modül Bazlı Lisanslama (Ruling 64) — self-servis öğelerin (`module: 'PM'|'SM'`, privileges [])
    // uçları RequireModuleSeat taşır: ölçümlü modülde koltuğu olmayan kişi öğeyi görüp tıklayınca
    // 403 sayfasına düşerdi (mobil hub kartlarının web ikizi). hasSeat ölçümsüz/varsayılan modülde
    // ve seats alanı olmayan eski oturumda true döner — ölçümsüz kiracıda menü bugünkü gibi.
    if (item.module && !hasSeat(item.module)) return false;
    if (item.privileges?.includes('superAdmin')) return false;
    // 'tenant.manage' = superAdmin OR multi-tenant-scoped (holding admin).
    if (item.privileges?.includes('tenant.manage')) return canManageTenants;

    // moduleGated items require the tenant to actually have the module —
    // privileges alone don't matter if the subscription is missing. Module
    // code resolution prefers the explicit `module` field over inference
    // from privileges[0] (see menuTree.moduleGateCode — ledger T9 fix).
    // Modül Bazlı Lisanslama, Düzeltme turu 2: aynı dal artık kullanıcının o
    // modülde koltuğu olup olmadığını da kontrol ediyor (menuTree.
    // moduleSubscriptionGate — saf fonksiyon, testi menuTree.test.js'te).
    // Koltuksuz bir enterpriseAdmin'in joker ayrıcalıkla menüde görüp
    // tıklayınca backend'den 403 aldığı hata sınıfını önler.
    if (!moduleSubscriptionGate(item, { hasModule, hasSeat })) return false;

    if (!item.privileges || item.privileges.length === 0) return true;
    if (isEnterpriseAdmin && !item.moduleGated) return true;
    if (item.privileges.some((priv) => user?.privileges?.includes(priv))) return true;
    // Faz 3 / PM-01 + PM-06 — OR-kapısı: "şu ayrıcalıklardan biri VEYA sunucudan
    // gelen şu kullanıcı bayrağı". leave-balance-report'un elle yazılmış
    // (canWrite('LM') || user?.hasSubordinates) dalının genelleştirilmiş hâli.
    // `requiresUserFlag`ın (AND — bayrak yoksa öğe tamamen gizli) AKSİNE burada
    // bayrak ayrıcalığa EK bir yoldur: PM.Read'i olmayan amir "Hedef Yönetimi"
    // ve "Ücret Artış Turu" girdilerini görür, astı olmayan çalışan GÖRMEZ
    // (privileges: [] yapmak ona kalıcı ölü bir girdi açardı — TD-31 kusuru).
    // Kural SAF fonksiyonda (menuTree.userFlagGate), testi orada.
    if (userFlagGate(item, user)) return true;

    // Self-service fallthrough: a gated group whose module IS active stays
    // visible when it contains entries open to every employee (privileges: []),
    // e.g. "Yan Haklarım" under Benefits — the employee holds no BN privilege
    // but must still reach their own records.
    return Boolean(
      item.moduleGated &&
      item.children?.some((child) => !child.privileges || child.privileges.length === 0)
    );
  };

  // Recursively filters the full 3-level tree down to what this user may see
  // (gating applies at every level — see menuTree.filterAccessible), then
  // locates the root→leaf key trail for the current route within that
  // filtered tree so we never auto-open/highlight a group the user can't see.
  // Kilitli-görünür deseni (yalnız lockedWhenUnsubscribed taşıyan gruplar; bugün
  // sadece Yetenek & Gelişim, bkz. MenuConfig.js) menuTree.lockGated'te yaşar —
  // saf fonksiyon, testleri menuTree.test.js'te.
  const gatedMenuConfig = lockGated(menuConfig, { hasModule, isSuperAdmin });

  const visibleConfig = filterAccessible(gatedMenuConfig, hasAccess);
  const activeTrail = findActiveTrail(visibleConfig, location.pathname);
  const activeKey = activeTrail[activeTrail.length - 1];
  const activeSet = new Set(activeTrail);

  // Search: label-matched Turkish-insensitively against the accessible tree
  // (menuTree.searchMenu keeps ancestors of any matching descendant so a
  // deep page hit still surfaces its parent group(s)).
  const [query, setQuery] = useState('');
  const label = (n) => t(n.labelKey);
  const searchedConfig = query.trim() ? searchMenu(visibleConfig, query.trim(), label) : visibleConfig;

  const [openMenus, setOpenMenus] = useState(() => {
    const initial = {};
    findActiveTrail(filterAccessible(gatedMenuConfig, hasAccess), location.pathname)
      .forEach((k) => { initial[k] = true; });
    return initial;
  });

  // Auto-open every menu on the active trail — never auto-close (user toggles
  // for menus outside the trail are preserved).
  useEffect(() => {
    const trail = findActiveTrail(filterAccessible(gatedMenuConfig, hasAccess), location.pathname);
    if (trail.length === 0) return;
    setOpenMenus((prev) => {
      let next = prev;
      trail.forEach((k) => {
        if (!prev[k]) { if (next === prev) next = { ...prev }; next[k] = true; }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Menü ikon dili GRADYAN'dır ve aktiflikten bağımsız korunur (aktiflik kalın
  // metin + renkle verilir). Tek istisna seviye-0 aktif: dolu mavi zemin
  // üstünde gradyan/mavi kaybolur, ikon beyaz olur.
  const renderIcon = (Icon, active, level = 0) =>
    active && level === 0
      ? <Icon size={22} color="#fff" />
      : <GradientIcon icon={Icon} size={22} />;

  const menuItemStyles = {
    button: ({ level, active }) => {
      if (level === 0) {
        return {
          color: active ? '#fff' : theme.colors.text.primary,
          backgroundColor: active ? theme.colors.primary.main : 'transparent',
          fontFamily: theme.metrics.font.family,
          fontSize: '16px',
          fontWeight: active ? theme.metrics.font.weight.semiBold : theme.metrics.font.weight.regular,
          padding: '10px 12px',
          margin: '2px 8px',
          borderRadius: theme.metrics.radius.medium,
          height: 'auto',
          minHeight: 'auto',
          '&:hover': {
            backgroundColor: active ? theme.colors.primary.dark : 'rgba(2, 145, 239, 0.06)',
            color: active ? '#fff' : theme.colors.text.primary,
          },
        };
      }
      if (level === 1) {
        return {
          color: active ? theme.colors.primary.main : theme.colors.text.primary,
          backgroundColor: 'transparent',
          fontFamily: theme.metrics.font.family,
          fontSize: '15px',
          fontWeight: active ? theme.metrics.font.weight.semiBold : theme.metrics.font.weight.regular,
          padding: '8px 12px',
          margin: '2px 8px 2px 28px',
          borderRadius: theme.metrics.radius.medium,
          height: 'auto',
          minHeight: 'auto',
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.colors.primary.main,
          },
        };
      }
      // level 2+ (page rows nested inside a module/sub-group) — deeper indent
      // and a touch smaller than the module row above them.
      return {
        color: active ? theme.colors.primary.main : theme.colors.text.primary,
        backgroundColor: 'transparent',
        fontFamily: theme.metrics.font.family,
        fontSize: '14px',
        fontWeight: active ? theme.metrics.font.weight.semiBold : theme.metrics.font.weight.regular,
        padding: '7px 12px',
        margin: '2px 8px 2px 44px',
        borderRadius: theme.metrics.radius.medium,
        height: 'auto',
        minHeight: 'auto',
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.colors.primary.main,
        },
      };
    },
    icon: { marginRight: 12, width: 22, minWidth: 22, height: 22 },
    label: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    subMenuContent: ({ level }) => ({
      // Inline (expanded sidebar): transparent so child items inherit page bg.
      // Popper (collapsed sidebar, level 0): solid white card.
      backgroundColor: level === 0 ? '#fff' : 'transparent',
      boxShadow: level === 0 ? '0 4px 24px rgba(0, 66, 66, 0.12)' : 'none',
      borderRadius: level === 0 ? '6px' : 0,
    }),
  };

  // Recursive 3-level render: category → module → page (plus the pinned
  // Benim group, which nests the same way). Leaves render as MenuItem, groups
  // as SubMenu; empty groups never reach here because visibleConfig has
  // already dropped them (see menuTree.filterAccessible). Onaylarım (Görev 7)
  // is a top-level leaf now — no children, so it renders as a plain MenuItem
  // alongside Benim, not as a SubMenu.
  const renderNode = (item, level) => {
    const Icon = item.icon;
    if (!item.children) {
      const isActive = item.key === activeKey;
      const subIcon = item.locked ? <LockIcon size={14} /> : <Dot $active={isActive} />;
      const badges = { performanceInbox: inboxBadge, approvals: approvalsBadge };
      const badge = item.badge ? badges[item.badge] : null;
      return (
        <MenuItem
          key={item.key}
          icon={level === 0 ? renderIcon(Icon, isActive) : subIcon}
          active={isActive}
          suffix={badge ? <CountBadge>{badge}</CountBadge> : undefined}
          onClick={() => { history.push(item.path); if (onNavigate) onNavigate(); }}
        >
          {t(item.labelKey)}
        </MenuItem>
      );
    }
    const active = activeSet.has(item.key);
    return (
      <SubMenu
        key={item.key}
        className={item.variant === 'category' ? 'menu-category' : undefined}
        icon={Icon ? renderIcon(Icon, active, level) : undefined}
        label={t(item.labelKey)}
        active={active}
        open={query.trim() ? true : !!openMenus[item.key]}
        onOpenChange={(isOpen) => {
          if (query.trim()) return;
          setOpenMenus((prev) => ({ ...prev, [item.key]: isOpen }));
        }}
      >
        {item.children.map((child) => renderNode(child, level + 1))}
      </SubMenu>
    );
  };

  // İşe hazırlık kilidi: menü tek öğeye iner. menuConfig hiç işlenmez.
  if (isPreboardingLocked) {
    const lockedActive = location.pathname === '/my-preboarding'
      || location.pathname.startsWith('/my-preboarding/');
    return (
      <Menu menuItemStyles={menuItemStyles} rootStyles={{ paddingTop: 8 }}>
        <MenuItem
          icon={renderIcon(Sparkles, lockedActive)}
          active={lockedActive}
          onClick={() => { history.push('/my-preboarding'); if (onNavigate) onNavigate(); }}
        >
          {t('menu.myPreboarding')}
        </MenuItem>
      </Menu>
    );
  }

  const mainItems = searchedConfig.filter((item) => !item.isFooter);
  const footerItems = searchedConfig.filter((item) => item.isFooter);

  return (
    <>
      <CategoryLabelStyle />
      {!collapsed && (
        <SearchBox>
          <SearchField>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('menu.searchPlaceholder')}
              aria-label={t('menu.searchPlaceholder')}
            />
            {query && (
              <ClearButton
                type="button"
                onClick={() => setQuery('')}
                aria-label={t('menu.searchClear')}
              >
                <X size={14} aria-hidden="true" />
              </ClearButton>
            )}
            <SearchAction aria-hidden="true">
              <Search size={16} aria-hidden="true" />
            </SearchAction>
          </SearchField>
        </SearchBox>
      )}
      <Menu menuItemStyles={menuItemStyles} rootStyles={{ paddingTop: 8 }}>
        {mainItems.length > 0
          ? mainItems.map((item) => renderNode(item, 0))
          : searchedConfig.length === 0 && query.trim() && <NoResults>{t('menu.searchNoResults')}</NoResults>}
      </Menu>
      {footerItems.length > 0 && (
        <Menu
          menuItemStyles={menuItemStyles}
          rootStyles={{
            marginTop: 'auto',
            borderTop: '1px solid #E8ECF0',
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          {footerItems.map((item) => renderNode(item, 0))}
        </Menu>
      )}
    </>
  );
}
